import httpClient from './httpClient';
import { parseApiError } from '../utils/errorHandler';
import { validateDeliveryDateRange } from '../utils/deliveryDateRange';
import { mapDeliveryStatusCountItem } from './deliveryService';
import { OPERATIONAL_METRIC_VALUE_TYPES } from '../constants/operationalMetricCatalog';
import { fillTimeSeriesGaps } from '../utils/operationalMetricUtils';

const DEFAULT_RANKING_LIMIT = 5;
const MIN_RANKING_LIMIT = 1;
const MAX_RANKING_LIMIT = 30;

/**
 * @param {number | string | undefined} rankingLimit
 * @returns {number | undefined}
 */
export function normalizeRankingLimit(rankingLimit) {
    if (rankingLimit == null || rankingLimit === '') {
        return undefined;
    }
    const value = Number(rankingLimit);
    if (!Number.isInteger(value) || value < MIN_RANKING_LIMIT || value > MAX_RANKING_LIMIT) {
        throw new Error('O limite do ranking deve ser um inteiro entre 1 e 30.');
    }
    return value;
}

/**
 * Builds query params for GET /api/painel-operacional and GET /api/metricas-operacionais.
 * @param {{ customerId?: string | number, startDate?: string, endDate?: string, rankingLimit?: number, keys?: string[] }} params
 * @returns {URLSearchParams}
 */
export function buildOperationalDashboardParams({
    customerId,
    startDate,
    endDate,
    rankingLimit,
    keys,
} = {}) {
    if (startDate && endDate) {
        const validation = validateDeliveryDateRange(startDate, endDate);
        if (!validation.valid) {
            throw new Error(validation.message);
        }
    }

    const params = new URLSearchParams();
    (keys || []).forEach((key) => {
        if (key) {
            params.append('keys', String(key));
        }
    });
    if (customerId != null && String(customerId).trim() !== '') {
        params.set('customerId', String(customerId));
    }
    if (startDate) {
        params.set('startDate', startDate);
    }
    if (endDate) {
        params.set('endDate', endDate);
    }
    const limit = normalizeRankingLimit(rankingLimit);
    if (limit != null) {
        params.set('rankingLimit', String(limit));
    }
    return params;
}

function toFiniteNumber(value, fallback = 0) {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
}

function normalizePoints(rawPoints, filter) {
    const points = Array.isArray(rawPoints) ? rawPoints : [];
    return fillTimeSeriesGaps(points, filter.startDate, filter.endDate);
}

/**
 * @param {*} data
 * @param {string} valueType
 * @param {{ startDate?: string, endDate?: string }} filter
 */
export function normalizeMetricData(data, valueType, filter = {}) {
    switch (valueType) {
        case OPERATIONAL_METRIC_VALUE_TYPES.SCALAR: {
            const value = toFiniteNumber(data?.value, 0);
            return { value };
        }
        case OPERATIONAL_METRIC_VALUE_TYPES.RATE: {
            const rateRaw = data?.rate;
            const rate = rateRaw == null || rateRaw === '' ? null : toFiniteNumber(rateRaw, null);
            return {
                rate,
                value: rate,
                numerator: toFiniteNumber(data?.numerator, 0),
                denominator: toFiniteNumber(data?.denominator, 0),
            };
        }
        case OPERATIONAL_METRIC_VALUE_TYPES.MONEY: {
            return {
                value: toFiniteNumber(data?.value, 0),
                currency: data?.currency ? String(data.currency) : 'BRL',
            };
        }
        case OPERATIONAL_METRIC_VALUE_TYPES.STATUS_COUNT_LIST: {
            if (!Array.isArray(data)) {
                return [];
            }
            return data.map((item) => mapDeliveryStatusCountItem(item));
        }
        case OPERATIONAL_METRIC_VALUE_TYPES.TIME_SERIES: {
            return normalizePoints(data, filter);
        }
        case OPERATIONAL_METRIC_VALUE_TYPES.VOLUME_SERIES: {
            const points = normalizePoints(data?.points, filter);
            const totalFromApi = data?.total;
            const total = totalFromApi == null
                ? points.reduce((sum, point) => sum + point.value, 0)
                : toFiniteNumber(totalFromApi, 0);
            return { total, points };
        }
        case OPERATIONAL_METRIC_VALUE_TYPES.RANKING: {
            if (!Array.isArray(data)) {
                return [];
            }
            return data.map((entry) => ({
                id: entry?.id != null ? Number(entry.id) : 0,
                label: entry?.label != null ? String(entry.label) : '',
                value: toFiniteNumber(entry?.value, 0),
            }));
        }
        default:
            return data ?? null;
    }
}

/**
 * @param {{ key?: string, valueType?: string, data?: *, computedAt?: string }} raw
 * @param {{ startDate?: string, endDate?: string }} filter
 */
export function normalizeOperationalMetric(raw = {}, filter = {}) {
    const key = raw.key ? String(raw.key) : '';
    const valueType = raw.valueType ? String(raw.valueType) : '';
    return {
        key,
        valueType,
        data: normalizeMetricData(raw.data, valueType, filter),
        computedAt: raw.computedAt ? String(raw.computedAt) : null,
    };
}

/**
 * @param {*} json
 * @returns {{ preset: string | null, filter: Object, metrics: Array }}
 */
export function normalizeOperationalDashboardResponse(json = {}) {
    const filter = {
        customerId: json.filter?.customerId ?? null,
        startDate: json.filter?.startDate ? String(json.filter.startDate).slice(0, 10) : null,
        endDate: json.filter?.endDate ? String(json.filter.endDate).slice(0, 10) : null,
        driverId: json.filter?.driverId ?? null,
        rankingLimit: json.filter?.rankingLimit ?? null,
    };

    const metricsRaw = Array.isArray(json.metrics) ? json.metrics : [];
    const metrics = metricsRaw.map((item) => normalizeOperationalMetric(item, filter));

    return {
        preset: json.preset != null ? String(json.preset) : null,
        filter,
        metrics,
    };
}

/**
 * GET /api/metricas-operacionais/tipos
 * @returns {Promise<Array<{ key: string, label?: string, description?: string, valueType?: string }>>}
 */
export async function fetchOperationalMetricTypes() {
    const response = await httpClient.get('/api/metricas-operacionais/tipos');
    if (!response.ok) {
        throw await parseApiError(response);
    }
    const json = await response.json();
    if (!Array.isArray(json)) {
        return [];
    }
    return json
        .filter((item) => item && item.key)
        .map((item) => ({
            key: String(item.key),
            label: item.label ? String(item.label) : String(item.key),
            description: item.description ? String(item.description) : '',
            valueType: item.valueType ? String(item.valueType) : '',
        }));
}

/**
 * GET /api/metricas-operacionais
 * @param {{ keys: string[], customerId?: string | number, startDate?: string, endDate?: string, rankingLimit?: number }} params
 */
export async function fetchOperationalMetrics(params = {}) {
    const keys = params.keys || [];
    if (!keys.length) {
        throw new Error('Informe ao menos uma métrica.');
    }
    const query = buildOperationalDashboardParams(params);
    const qs = query.toString();
    const path = qs ? `/api/metricas-operacionais?${qs}` : '/api/metricas-operacionais';
    const response = await httpClient.get(path);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    const json = await response.json();
    return normalizeOperationalDashboardResponse(json);
}

/**
 * GET /api/painel-operacional
 * @param {{ customerId?: string | number, startDate?: string, endDate?: string, rankingLimit?: number }} params
 */
export async function fetchOperationalDashboard(params = {}) {
    const query = buildOperationalDashboardParams(params);
    const qs = query.toString();
    const path = qs ? `/api/painel-operacional?${qs}` : '/api/painel-operacional';
    const response = await httpClient.get(path);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    const json = await response.json();
    return normalizeOperationalDashboardResponse(json);
}

const operationalMetricService = {
    DEFAULT_RANKING_LIMIT,
    buildOperationalDashboardParams,
    normalizeOperationalMetric,
    normalizeOperationalDashboardResponse,
    normalizeMetricData,
    normalizeRankingLimit,
    fetchOperationalMetricTypes,
    fetchOperationalMetrics,
    fetchOperationalDashboard,
};

export default operationalMetricService;
